import { useEffect, useState } from 'react';
import styles from './style.module.scss';
import { Text } from '../typography/Text/Text';
import { Caption } from '../typography/Caption/Caption';
import { useNav } from 'src/onboarding/hooks/nav';
import { t } from '@web-solutions/module-localization';

interface HorizontalLoaderProps {
  duration?: number;
  onComplete?: () => void;
  show?: boolean;
  className?: string;
}

const HorizontalLoader = ({
  duration,
  onComplete,
  show = true,
  className = '',
}: HorizontalLoaderProps) => {
  const { onNext } = useNav();

  const [progress, setProgress] = useState(0);
  const [currentText, setCurrentText] = useState('');

  const steps = [
    { threshold: 0, text: t('onboarding.faceScanPopup.analyzing')},
    { threshold: 10, text: t('onboarding.faceScanPopup.analyzing') },
    { threshold: 20, text: t('onboarding.faceScanPopup.evaluating') },
    { threshold: 40, text: t('onboarding.faceScanPopup.building') },
    { threshold: 90, text: t('onboarding.faceScanPopup.analyzing') },
    { threshold: 100, text: t('onboarding.faceScanPopup.finalizing') },
  ];

  useEffect(() => {
    const currentStep = steps.filter((step) => progress >= step.threshold).pop();

    if (currentStep) {
      setCurrentText(currentStep.text);
    }
  }, [progress]);

  useEffect(() => {
    if (!show) return;

    let animationFrame: number;
    const startTimeRef = { current: null as number | null };

    const animate = (timestamp: number) => {
      if (startTimeRef.current === null) startTimeRef.current = timestamp;

      const elapsed = timestamp - startTimeRef.current;
      const currentProgress = Math.min((elapsed / duration) * 100, 100);

      setProgress(currentProgress);

      if (currentProgress < 100) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        // onNext();
        onComplete?.();
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [show, duration, onComplete]);

  const displayProgress = Math.floor(progress);

  if (!show) return null;

  return (
    <div className={`${styles.loader} ${className}`}>
      <div className={styles.overlay} />

      <div className={styles.content}>
        <div className={styles.progressInfo}>
          <Text size='m' bold className={styles.percent}>
            {displayProgress}%
          </Text>

          <div className={styles.barContainer}>
            <div className={styles.bar}>
              <div
                className={styles.barFill}
                style={{ width: `${progress}%` }}
                data-testid='loader-bar'
              />
            </div>
          </div>

          <Caption size='l' className={styles.text} data-testid='loader-text'>
            {currentText}
          </Caption>
        </div>
      </div>
    </div>
  );
};

export default HorizontalLoader;
